import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { marked } from "marked";
import { Eye, Pencil } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type { NewStaticPage, StaticPage } from "@shared/schema";

export function AdminPageEdit({ pageId }: { pageId: string | null }) {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const isNew = !pageId;
  const [showPreview, setShowPreview] = useState(false);

  const q = useQuery({
    queryKey: ["admin", "page", pageId],
    queryFn: () => api<StaticPage>(`/api/admin/pages/${pageId}`),
    enabled: !isNew,
  });

  const [draft, setDraft] = useState<Partial<NewStaticPage>>({
    slug: "",
    title: "",
    content: "",
    isPublished: true,
  });

  useEffect(() => {
    if (q.data) {
      setDraft({
        slug: q.data.slug,
        title: q.data.title,
        content: q.data.content,
        isPublished: q.data.isPublished,
      });
    }
  }, [q.data]);

  const html = useMemo(() => {
    if (!draft.content) return "";
    return marked.parse(draft.content, { async: false }) as string;
  }, [draft.content]);

  const create = useMutation({
    mutationFn: () =>
      api<StaticPage>("/api/admin/pages", { method: "POST", body: draft }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["admin", "pages"] });
      navigate(`/pages/${p.id}`);
    },
  });

  const update = useMutation({
    mutationFn: () =>
      api<StaticPage>(`/api/admin/pages/${pageId}`, {
        method: "PATCH",
        body: draft,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pages"] });
      qc.invalidateQueries({ queryKey: ["admin", "page", pageId] });
      qc.invalidateQueries({ queryKey: ["page"] });
    },
  });

  const saving = create.isPending || update.isPending;
  const lastError = create.error ?? update.error;

  if (!isNew && q.isLoading) {
    return <div className="text-brand-500">Betöltés...</div>;
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (isNew) create.mutate();
        else update.mutate();
      }}
      className="space-y-6 max-w-5xl"
    >
      <header>
        <h1 className="text-2xl font-bold">
          {isNew ? "Új oldal" : draft.title}
        </h1>
        {!isNew && draft.slug && (
          <p className="text-sm text-brand-500 mt-1">/o/{draft.slug}</p>
        )}
      </header>

      <div className="card p-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Cím *</label>
          <input
            required
            className="input"
            value={draft.title ?? ""}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Slug (URL) *</label>
          <input
            required
            pattern="[a-z0-9-]+"
            className="input font-mono"
            value={draft.slug ?? ""}
            onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <input
            id="isPublished"
            type="checkbox"
            checked={draft.isPublished ?? true}
            onChange={(e) =>
              setDraft({ ...draft, isPublished: e.target.checked })
            }
          />
          <label htmlFor="isPublished" className="text-sm">
            Publikálva
          </label>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-brand-100 bg-brand-50">
          <div className="text-xs uppercase tracking-widish font-semibold text-brand-700">
            Tartalom (Markdown)
          </div>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => setShowPreview((v) => !v)}
          >
            {showPreview ? <Pencil size={12} /> : <Eye size={12} />}
            {showPreview ? "Szerkesztés" : "Előnézet"}
          </button>
        </div>
        {showPreview ? (
          <div
            className="prose-page p-6 min-h-[400px]"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <textarea
            rows={20}
            className="w-full px-4 py-3 font-mono text-sm bg-white border-0 focus:outline-none resize-y"
            value={draft.content ?? ""}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            placeholder="# Cím

Bekezdés szövege.

## Alcím

- Lista elem
- Lista elem 2"
          />
        )}
      </div>

      {lastError && (
        <div className="text-sm text-red-600">
          {lastError instanceof ApiError
            ? `${lastError.status}: ${lastError.message}`
            : "Mentési hiba"}
        </div>
      )}

      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? "Mentés..." : "Mentés"}
        </button>
      </div>
    </form>
  );
}
